import type { Dispatch, PropsWithChildren, SetStateAction } from "react";
import { createContext, useContext, useState } from "react";

import type { CategoryWithProducts } from "@/features/products/models";
import type { CreditAmount } from "@/features/referral/models";
import type { PaymentMethod, Price } from "@/shared/domains";

type SelectedCategory = CategoryWithProducts["slug"];
export type SelectedProduct = CategoryWithProducts["products"][number];

interface ShopContextValue {
  selectedCategory: SelectedCategory | null;
  selectedProduct?: SelectedProduct;
  selectedPrice?: Price;
  selectedCreditAmount?: CreditAmount;
  selectedPaymentMethodType?: PaymentMethod["type"];

  actions: {
    setSelectedCategory: Dispatch<SetStateAction<SelectedCategory | null>>;
    selectProduct: (product: SelectedProduct) => void;
    removeProduct: VoidFunction;
    selectPrice: (price: Price) => void;
    selectCreditAmount: (amount?: CreditAmount) => void;
    removeCreditAmount: VoidFunction;
    selectPaymentMethodType: (
      paymentMethodType?: PaymentMethod["type"],
    ) => void;
  };
}

const ShopContext = createContext<ShopContextValue | null>(null);

export const useShopContext = () => {
  const context = useContext(ShopContext);

  if (!context) {
    throw new Error(
      "useShopContext must be used within a <ShopContextProvider />",
    );
  }

  return context;
};

export const ShopContextProvider = ({ children }: PropsWithChildren) => {
  const [selectedCategory, setSelectedCategory] =
    useState<SelectedCategory | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct>();
  const [selectedPrice, setSelectedPrice] = useState<Price>();
  const [selectedCreditAmount, setSelectedCreditAmount] =
    useState<CreditAmount>();
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] =
    useState<PaymentMethod["type"]>();

  const selectProduct = (product: SelectedProduct) => {
    setSelectedPrice(undefined);
    setSelectedProduct(product);
  };
  const removeProduct = () => setSelectedProduct(undefined);
  const removeCreditAmount = () => setSelectedCreditAmount(undefined);
  const selectPrice = (price: Price) => setSelectedPrice(price);
  const selectPaymentMethodType = (paymentMethodType?: PaymentMethod["type"]) =>
    setSelectedPaymentMethodType(paymentMethodType);

  return (
    <ShopContext.Provider
      value={{
        selectedCategory,
        selectedProduct,
        selectedPrice,
        selectedCreditAmount,
        selectedPaymentMethodType,
        actions: {
          setSelectedCategory,
          selectProduct,
          removeProduct,
          selectPrice,
          selectCreditAmount: setSelectedCreditAmount,
          removeCreditAmount,
          selectPaymentMethodType,
        },
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};
